import React, { useState, useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import axios from 'axios';

const ModifyPDFPage = () => {
  const [studentName, setStudentName] = useState('');
  const [message, setMessage] = useState('');
  const signatureRef = useRef(null);

  // Helper function to convert Uint8Array to base64
  const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!studentName) {
      setMessage('Please provide a student name');
      return;
    }
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      setMessage('Please provide a signature');
      return;
    }

    // Get the signature as a PNG data URL
    const instructorSignature = signatureRef.current.toDataURL('image/png');

    // Construct the verification object
    const verification = {
      studentName,
      eventTitle: "Test Event",
      eventDate: "2023-10-01",
      hours: "5",
      instructorName: "Jane Smith",
      instructorSignature,
      verifierName: "Alice Brown",
      concentration: "Computer Science",
      job: {
        title: "Developer",
        concentration: { title: "Web Dev" },
        otherConcentrations: [],
        description: "Builds apps",
      },
      //folderLocation: "public/events",
      folderLocation: "public/temp/tests",
      filename: `${Date.now()}-verification_doc`,
    };

    try {
      // Fetch the existing PDF document from S3
      const url = 'https://acuitycte-files.s3.us-west-2.amazonaws.com/public/pdf-templates/Student-Verification-Form.pdf';
      const template_response = await fetch(url);
      if (!template_response.ok) throw new Error('Failed to fetch PDF');
      const existingPdfBytes = await template_response.arrayBuffer();

      // Load and modify the PDF
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const testSignature = await pdfDoc.embedPng(verification.instructorSignature);
      const page1 = pdfDoc.getPages()[0];

      // Modify the PDF (as per original script)
      page1.drawText("Event Title: " + verification.eventTitle, {
        x: 60,
        y: 560,
        size: 12,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      });
      page1.drawRectangle({
        x: 58,
        y: 536,
        width: 250,
        height: 18,
        color: rgb(1, 1, 1),
      });
      page1.drawRectangle({
        x: 56,
        y: 465,
        width: 250,
        height: 18,
        color: rgb(1, 1, 1),
      });
      page1.drawText(verification.eventDate, {
        x: 60,
        y: 465,
        size: 12,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      });
      page1.drawText(verification.studentName, {
        x: 90,
        y: 590,
        size: 12,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      });
      page1.drawText(verification.hours, {
        x: 90,
        y: 384,
        size: 12,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      });
      page1.drawText(verification.instructorName, {
        x: 90,
        y: 298,
        size: 12,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      });
      page1.drawImage(testSignature, {
        x: 90,
        y: 210,
        width: 250,
        height: 15,
      });
      page1.drawRectangle({
        x: 200,
        y: 75,
        width: 250,
        height: 60,
        color: rgb(1, 1, 1),
      });
      page1.drawText(verification.verifierName, {
        x: 90,
        y: 197,
        size: 12,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      });
      const job = verification.job;
      page1.drawText("Enrolled Concentration: " + verification.concentration, {
        x: 50,
        y: 150,
        size: 12,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      });
      page1.drawText("Job Title: " + job.title, {
        x: 50,
        y: 120,
        size: 12,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      });
      const jobConcentrations =
        job.otherConcentrations.length > 0
          ? job.concentration.title + " with " + verification.jobsOtherConcentrations
          : job.concentration.title;
      page1.drawText(
        "Job Concentrations: " + jobConcentrations + "\nJob Description: " + job['description'],
        {
          x: 50,
          y: 105,
          size: 10,
          lineHeight: 11,
          maxWidth: 500,
          font: helveticaFont,
          color: rgb(0, 0, 0),
        }
      );

      // Save the modified PDF as base64
      const pdfBytes = await pdfDoc.save();
      const base64String = arrayBufferToBase64(pdfBytes);

      // Send to the backend
      const response = await axios.post('/api/save-pdf',
        {
          location: verification.folderLocation,
          filename: verification.filename,
          pdffile: base64String,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      setMessage(
        <span>
          PDF saved successfully:{" "}
          <a href={response.data.url} target="_blank" rel="noopener noreferrer">
            {response.data.url}
          </a>
        </span>
      );
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    }
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
      <h2>Modify PDF</h2>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>
            Student Name:
            <input
              type="text"
              value={studentName}
              onChange={(e) => setStudentName(e.target.value)}
              required
              style={{ width: '100%', padding: '8px', marginTop: '5px' }}
            />
          </label>
        </div>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>
            Instructor Signature:
          </label>
          {SignatureCanvas ? (
            <SignatureCanvas
              ref={signatureRef}
              canvasProps={{
                width: 500,
                height: 200,
                style: { border: '1px solid #ccc', backgroundColor: '#fff' },
              }}
            />
          ) : (
            <p>Error: Signature pad failed to load</p>
          )}
          <button
            type="button"
            onClick={clearSignature}
            style={{ marginTop: '10px', padding: '8px 16px' }}
          >
            Clear Signature
          </button>
        </div>
        <button
          type="submit"
          style={{ padding: '10px 20px', backgroundColor: '#1D4E89', color: '#fff', border: 'none' }}
        >
          Submit
        </button>
      </form>
      {message && <p>{message}</p>}
    </div>
  );
};

export default ModifyPDFPage;